import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import DistributionDetails from './DistributionDetails';
import DistributionSummary from './DistributionSummary';
import { initialDistributionDetailsState, initialDistributionSummaryState } from './initialState';

const initialState = {
  ...initialDistributionDetailsState,
  ...initialDistributionSummaryState,
};

const CreateDistributionWizard = ({ isFetchingShipment, onClose, shipmentData }) => {
  const { shipmentId } = useParams();
  const [step, setStep] = useState(1);
  const [distributionsState, setDistributionsState] = useState({
    ...initialState,
    shipmentId,
  });
  const [confirmDialogOpened, setConfirmDialogOpened] = useState(false);
  const [totalProposedVolume, setTotalProposedVolume] = useState(0);
  const [availableVolumeExceeded, setAvailableVolumeExceeded] = useState(false);

  const initialValidationState = {
    chainRecipients: '',
    inventoryOwner: '',
    locationId: '',
    volume: '',
  };

  const [validationMessages, setValidationMessages] = useState([initialValidationState]);

  const handleNext = () => {
    setStep(R.inc);
  };
  const handlePrev = () => {
    setStep(R.dec);
  };
  const handleReset = () => {
    setStep(1);
    setDistributionsState({
      ...initialState,
      shipmentId,
    });
    setValidationMessages([initialValidationState]);
  };

  const handleClose = () => {
    // shipmentId is set automatically, so we can't include it
    // in state comparisons, or it'll never be equal to initial state.
    const omitShipmentId = R.omit(['shipmentId']);

    if (R.equals(omitShipmentId(distributionsState), omitShipmentId(initialState))) {
      onClose();
    } else {
      setConfirmDialogOpened(true);
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpened(false);
  };

  const handleComplete = () => {
    onClose();
  };

  const stepProps = {
    availableVolumeExceeded,
    distributionsState,
    initialValidationState,
    isFetchingShipment,
    onClose: handleClose,
    onComplete: handleComplete,
    onNext: handleNext,
    onPrev: handlePrev,
    onReset: handleReset,
    setDistributionsState,
    setValidationMessages,
    shipmentData,
    totalProposedVolume,
    validationMessages,
  };

  const originId = R.prop('originId', shipmentData);
  const productId = R.prop('productId', shipmentData);

  const shipmentProps = {
    originId,
    productId,
  };

  useEffect(() => {
    const { distributions } = distributionsState;
    const distributionsTotalVolume = R.reduce(
      (accum, item) => accum + (Number(item.volume) || 0),
      0
    );
    const proposedVolume = R.add(
      R.prop('totalDistributedVolume', shipmentData),
      distributionsTotalVolume(distributions)
    );

    setTotalProposedVolume(proposedVolume);
    setAvailableVolumeExceeded(R.gt(proposedVolume, R.prop('volume', shipmentData)));

    // we don't want to re-total distributed volume
    // after successful API create (or it would be incorrect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributionsState]);

  return (
    <Dialog
      fullScreen
      onClose={handleClose}
      open
      slotProps={{
        paper: { elevation: 0 },
      }}
    >
      {step === 1 && <DistributionDetails {...stepProps} {...shipmentProps} />}
      {step === 2 && <DistributionSummary {...stepProps} />}
      {confirmDialogOpened && (
        <Dialog
          maxWidth="xs"
          onClose={handleCloseConfirmDialog}
          open
          slotProps={{
            paper: { elevation: 10 },
          }}
          sx={{ '& .MuiDialog-paper': { height: 180, width: 330 } }}
        >
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogContent>
            Are you sure you want to leave this page? You have unsaved changes.
          </DialogContent>
          <DialogActions sx={{ pb: 2, px: 2 }}>
            <Button autoFocus onClick={onClose} variant="outlined">
              Continue
            </Button>
            <Button onClick={handleCloseConfirmDialog}>Keep Editing</Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

CreateDistributionWizard.propTypes = {
  isFetchingShipment: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shipmentData: PropTypes.shape({}).isRequired,
};

export default CreateDistributionWizard;
